import {gql, useMutation} from '@apollo/client';
import {ID, OfferType} from "../../../../../types";
import castAttributesFromModel, { ModelDefinitionType } from '../../../../../utils/castAttributesFromDefinition';
import { DOMAIN_FRAGMENT, LEVEL_FRAGMENT, OFFER_BASE_FRAGMENT, PATHWAY_FRAGMENT } from "../../../../../utils/fragments";

export const orderSearchDefinition: ModelDefinitionType = {
  location: {
    city: "String",
    lat: "Float",
    lng: "Float",
  },
  createdAtGteq: 'Date',
  createdAtLteq: 'Date',
  companyNameICont: "String",
  onlyFavorites: 'Boolean',
  typeOfContractEq: "String",
  sectorEq: "String",
  offerPublicationsDomainIdEq: "String",
  offerPublicationsPathwayIdEq: "String",
  offerPublicationsLevelIdEq: "String",
  keywordsICont: "String",
  publicationStateEq: "String",
  createdByEq: "String",
  proximity: "Integer",
}

export type OfferSearchType = {
  location?: {
    city?: string;
    lat?: string;
    lng?: string;
  };
  createdByEq?: string;
  onlyFavorites?: boolean;
  typeOfContractEq?: string;
  sectorEq?: string;
  companyNameICont?: string;
  offerPublicationsDomainIdEq?: string;
  offerPublicationsPathwayIdEq?: string;
  offerPublicationsLevelIdEq?: string;
  keywordsICont?: string;
  publicationStateEq?: string;
  proximity?: number
};

export const UPDATE_MULTIPLE_OFFER_STATUS_MUTATION = gql`
  mutation updateMultipleOfferStatus($input: UpdateMultipleOfferStatusInput!) {
    updateMultipleOfferStatus(input: $input) {
      flashMessages
      offers {
        id
        publicationState
      }
    } 
  }
`

const SHOW_OFFER_FRAGMENT = gql`
  fragment ShowOffer on Offer {
    ...OfferInfo
    viewCount
    publisher {
      id
      firstName
      lastName
    }
    offerPublications {
      id
      pathwayId
      levelId
      domainId
      domain {
        ...DomainInfo
      }
      pathway {
        ...PathwayInfo
      }
      level {
        ...LevelInfo
      }
    }
  }
  ${DOMAIN_FRAGMENT}
  ${LEVEL_FRAGMENT}
  ${PATHWAY_FRAGMENT}
  ${OFFER_BASE_FRAGMENT}
`

const CREATE_OFFER = gql`
  mutation createOffer($input: CreateOfferInput!) {
    createOffer(input: $input) {
      offer {
        ...OfferInfo
      }
    }
  }
  ${OFFER_BASE_FRAGMENT}
`;

const UPDATE_OFFER = gql`
  mutation updateOffer($input: UpdateOfferInput!) {
    updateOffer(input: $input) {
      offer {
        ...OfferInfo
      }
    }
  }
  ${OFFER_BASE_FRAGMENT}
`;

const DESTROY_OFFER = gql`
  mutation destroyOffer($input: DestroyOfferInput!) {
    destroyOffer(input: $input) {
      offer {
        id
      }
      flashMessages
    }
  }
`;

const UPDATE_STATUS = gql`
  mutation updateOfferStatus($input: UpdateOfferStatusInput!) {
    updateOfferStatus(input: $input) {
      flashMessages
    }
  }
`

const UPDATE_OFFER_PUBLICATIONS = gql`
  mutation updateOfferPublications($input: UpdateOfferPublicationsInput!) {
    updateOfferPublications(input: $input) {
      flashMessages
      offer {
        ...ShowOffer
      }
    }
  }
  ${SHOW_OFFER_FRAGMENT}
`

export const OFFERS_QUERY = gql`
  query offers($search: OffersSearch, $page: Int, $perPage: Int!) {
    offers(search: $search, page: $page, perPage: $perPage) {
      offers {
        id
        title
        companyName
        publicationState
        typeOfContract
        publishedAt
        location
        publisher {
          id
          firstName
          lastName
        }
        creator {
          id
          firstName
          lastName
        }
        city
        school {
          id
          title
        }
      }
      pagination {
        page
        totalPage
        perPage
        total
      }
    }
  }
`;

const offerPublicationDefinition: ModelDefinitionType = {
  id: "String",
  _destroy: "Boolean",
  levelId: "String",
  domainId: "String",
  pathwayId: "String"
}

const offerDefinition: ModelDefinitionType = {
  title: "String",
  companyDetails: "String",
  missionDetails: "String",
  profileDetails: "String",
  contactFirstName: "String",
  contactLastName: "String",
  contactEmail: "String",
  contactPhone: "String",
  keywords: "String",
  shared: "Boolean",
  location: {
    type: 'Places',
    options: {
      formattedAddress: 'location',
    }
  },
  companyName: "String",
  typeOfContract: "String",
  sector: 'String',
  minDurationMonths: 'Integer',
  applicationUrl: 'String',
  attachment: 'File'
}

export const GET_OFFER = gql`
  query offer($id: ID!) {
    offer(id: $id) {
      ...ShowOffer
      createdAt
      location
      applicationCount
      school {
        id
        title
      }
      applications {
        id
        createdAt
        student {
          id
          firstName
          lastName
        }
      }
      pdfFile {
        id
        url
      }
    }
  }
  ${SHOW_OFFER_FRAGMENT}
`;

const useOffer = (id?: ID, mutationOptions = {}) => {
  const [createOffer] = useMutation(CREATE_OFFER, {
    context: {
      hasUpload: true,
    },
    ...(mutationOptions || {})
  });
  const [updateOffer] = useMutation(UPDATE_OFFER, {
    context: {
      hasUpload: true,
    },
    ...(mutationOptions || {})
  });
  const [destroyOffer] = useMutation(DESTROY_OFFER, {
    ...(mutationOptions || {})
  });

  const [updateOfferStatus] = useMutation(UPDATE_STATUS, {
    ...(mutationOptions || {})
  })

  const [updateOfferPublications] = useMutation(UPDATE_OFFER_PUBLICATIONS, {
    ...(mutationOptions || {})
  })

  const onDestroy = (mutationOptions) => {
    destroyOffer({
      variables: {
        input: {
          id: id,
        },
      },
      ...(mutationOptions || {})
    });
  }

  const onStatusChange = (payload: string) => updateOfferStatus({
    variables: {
      input: {
        offerId: id,
        publicationState: payload
      }
    }
  })

  const onSave = (payload: OfferType, options = {}) => {
    if (id) {
      updateOffer({
        variables: {
          input: {
            id: id,
            attributes: castAttributesFromModel(offerDefinition, payload),
            ...(options.variables || {})
          },
        },
      });
    } else {
      createOffer({
        variables: {
          input: {
            attributes: castAttributesFromModel(offerDefinition, payload),
            ...(options.variables || {})
          },
        },
      });
    }
  };

  const onPublicationsSave = (payload: OfferType) => updateOfferPublications({
    variables: {
      input: {
        offerId: id,
        offerPublications: payload.offerPublications.map(p => castAttributesFromModel(offerPublicationDefinition, p))
      }
    }
  })

  return {
    onSave,
    onStatusChange,
    onDelete: onDestroy,
    onPublicationsSave
  };
};

export default useOffer;
